import { PopoverTrigger } from "@radix-ui/react-popover";
import type {
  AmericanTrailingReturns,
  BrazilianTrailingReturns,
} from "components/issuers/trailingReturns/TrailingReturns";
import TrailingReturns from "components/issuers/trailingReturns/TrailingReturns";
import { Popover, PopoverContent } from "components/ui/Popover";
import parseDateYmd from "lib/utils/parseDateYmd";
import trimEnd from "lodash/trimEnd";
import { CheckCircleIcon } from "lucide-react";
import numeral from "numeral";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import type { IBrazilianRealEstateFundIssuer, ITrailingReturns } from "types/graphqlTypes";

interface EarningsResultsEventDetailsProps {
  areFinancialsUpdated: boolean | null;
  issuer:
    | BrazilianTrailingReturns
    | AmericanTrailingReturns
    | Pick<IBrazilianRealEstateFundIssuer, "__typename" | "trailingReturns">;
  trailingReturns: ITrailingReturns[];
}

export function EarningsResultsEventDetails({
  areFinancialsUpdated,
  issuer,
  trailingReturns,
}: EarningsResultsEventDetailsProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <TrailingReturns issuer={issuer} trailingReturns={trailingReturns} />

      {areFinancialsUpdated && (
        <div className="col-start-2 inline-flex h-7 w-fit items-center rounded-md bg-blue-500/10 px-2 py-1.5 text-blue-600">
          <CheckCircleIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
          <span>
            <FormattedMessage defaultMessage="Atualizado" id="MgXuB0" />
          </span>
        </div>
      )}
    </div>
  );
}

interface PaymentOfDividendsEventDetailsProps {
  eventDate: string;
  excludingDate: string | null;
  recordDate: string | null;
  amountPaid: number | null;
  issuerCountry: "US" | "BR" | undefined;
}

export function PaymentOfDividendsEventDetails({
  amountPaid,
  eventDate,
  excludingDate,
  recordDate,
  issuerCountry,
}: PaymentOfDividendsEventDetailsProps) {
  const parsedEventDate = parseDateYmd(eventDate);

  return (
    <EventDetailsPopover key={`PaymentOfDividendsEventDetails/${eventDate}`}>
      <>
        {recordDate && (
          <>
            <strong className="font-bold text-slate-800">Data-com</strong>
            <time className="font-monospace" dateTime={parseDateYmd(recordDate).toString()}>
              {parseDateYmd(recordDate).toLocaleDateString("pt-BR")}
            </time>
          </>
        )}

        {excludingDate && (
          <>
            <strong className="font-bold text-slate-800">Data-ex</strong>
            <time className="font-monospace" dateTime={parseDateYmd(excludingDate).toString()}>
              {parseDateYmd(excludingDate).toLocaleDateString("pt-BR")}
            </time>
          </>
        )}

        <strong className="font-bold text-slate-800">Pagamento</strong>
        <time className="font-monospace" dateTime={parsedEventDate.toString()}>
          {parsedEventDate.toLocaleDateString("pt-BR")}
        </time>

        <strong className="font-bold text-slate-800">Valor</strong>
        <span className="font-mono">{`${issuerCountry === "US" ? "$" : "R$"} ${amountPaid?.toFixed(3)}`}</span>
      </>
    </EventDetailsPopover>
  );
}

interface PaymentOfStockDividendsEventDetailsProps {
  excludingDate: string;
  sharePaid: number;
}

export function PaymentOfStockDividendsEventDetails({
  excludingDate,
  sharePaid,
}: PaymentOfStockDividendsEventDetailsProps) {
  return (
    <EventDetailsPopover key={`PaymentOfStockDividendsEventDetails/${excludingDate}`}>
      <>
        <strong className="font-bold text-slate-800">Data-Ex</strong>
        <time className="font-mono" dateTime={parseDateYmd(excludingDate).toString()}>
          {parseDateYmd(excludingDate).toLocaleDateString("pt-BR")}
        </time>

        <strong className="font-bold text-slate-800">Ações Bonificadas</strong>
        <span className="font-mono">{sharePaid?.toFixed(3)}</span>
      </>
    </EventDetailsPopover>
  );
}

interface StockSplitEventDetailsProps {
  excludingDate: string;
  splitFrom: number | null;
  splitTo: number | null;
}

export function StockSplitEventDetails({ excludingDate, splitFrom, splitTo }: StockSplitEventDetailsProps) {
  const parsedExcludingDate = parseDateYmd(excludingDate);

  return (
    <EventDetailsPopover key={`StockSplitEventDetails/${excludingDate}`}>
      <>
        <strong className="font-bold text-slate-800">Data-Ex</strong>
        <time className="font-monospace" dateTime={parsedExcludingDate.toString()}>
          {parsedExcludingDate.toLocaleDateString("pt-BR")}
        </time>

        {splitFrom && splitTo && (
          <>
            <strong className="font-bold text-slate-800">Proporção</strong>
            <span className="font-monospace">
              {splitFrom}:{splitTo}
            </span>
          </>
        )}
      </>
    </EventDetailsPopover>
  );
}

interface RightsIssueEventDetailsProps {
  eventDate: string;
  recordDate: string | null;
  newShare: number | null;
  subscriptionPrice: number | null;
  issuerCountry: "US" | "BR" | undefined;
}

export function RightsIssueEventDetails({
  eventDate,
  recordDate,
  newShare,
  subscriptionPrice,
  issuerCountry,
}: RightsIssueEventDetailsProps) {
  const parsedEventDate = parseDateYmd(eventDate);

  return (
    <EventDetailsPopover key={`RightsIssueEventDetails/${eventDate}`}>
      <>
        {recordDate && (
          <>
            <strong className="font-bold text-slate-800">Data-com</strong>
            <time className="font-monospace" dateTime={parseDateYmd(recordDate).toString()}>
              {parseDateYmd(recordDate).toLocaleDateString("pt-BR")}
            </time>
          </>
        )}

        <strong className="font-bold text-slate-800">Pagamento</strong>
        <time dateTime={parsedEventDate.toString()}>{parsedEventDate.toLocaleDateString("pt-BR")}</time>

        {newShare && (
          <>
            <strong className="font-bold text-slate-800">Subscrição</strong>
            <span className="font-monospace">{newShare * 100}%</span>
          </>
        )}

        {subscriptionPrice && (
          <>
            <strong className="font-bold text-slate-800">Subscrição</strong>
            <span className="font-monospace">{`${issuerCountry === "US" ? "$" : "R$"}${subscriptionPrice}`}</span>
          </>
        )}
      </>
    </EventDetailsPopover>
  );
}

interface SpinOffEventDetailsProps {
  excludingDate: string;
  spinFrom: number;
  spinTo: number;
  numberOfShares: number;
  childCompanyName: string;
  childCompanyExchangeId: string;
  childCompanySymbol: string;
}

export function SpinOffEventDetails({
  excludingDate,
  spinFrom,
  spinTo,
  numberOfShares,
  childCompanyName,
  childCompanySymbol,
  childCompanyExchangeId,
}: SpinOffEventDetailsProps) {
  const formatValue = (value?: number | null) => {
    if (!value) {
      return "-";
    }

    return trimEnd(trimEnd("0", numeral(value).format("0.000").replace(".", ",")), ",");
  };

  return (
    <EventDetailsPopover key={`SpinOffEventDetails/${excludingDate}`}>
      <>
        <strong className="font-bold text-slate-800">Data-Ex</strong>
        <time className="font-monospace" dateTime={parseDateYmd(excludingDate).toString()}>
          {parseDateYmd(excludingDate).toLocaleDateString("pt-BR")}
        </time>

        <strong className="font-bold text-slate-800">Proporção</strong>
        <span className="font-monospace">
          {formatValue(spinFrom)}:{formatValue(spinTo)}
        </span>

        <strong className="font-bold text-slate-800">Quantidade de Ações</strong>
        <span className="font-monospace">{formatValue(numberOfShares)}</span>

        <strong className="font-bold text-slate-800">Nova Empresa</strong>
        <span className="font-monospace">{childCompanyName}</span>

        <strong className="font-bold text-slate-800">Listagem</strong>
        <span className="font-monospace">{childCompanyExchangeId}</span>

        <strong className="font-bold text-slate-800">Ticker</strong>
        <span className="font-monospace">{childCompanySymbol}</span>
      </>
    </EventDetailsPopover>
  );
}

interface EventDetailsPopoverProps {
  children: React.ReactElement;
}

function EventDetailsPopover({ children }: EventDetailsPopoverProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open}>
      <PopoverTrigger
        className="inline-flex select-none items-center rounded-md bg-slate-50 px-2 py-1.5 text-xs font-medium uppercase text-slate-600 hover:cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          setOpen((isOpen) => !isOpen);
        }}
      >
        Detalhes
      </PopoverTrigger>
      <PopoverContent className="grid w-max grid-cols-2 gap-2 rounded-xl border border-gray-200 p-2 text-xs normal-case text-gray-500 opacity-100 shadow-md">
        {children}
      </PopoverContent>
    </Popover>
  );
}
